import { useEffect } from "react";
import { CONTENT_FIRST_PUBLISHED, CONTENT_LAST_UPDATED } from "@/data/models";

/**
 * useSeoTitle — light per-route SEO helper. Sets `document.title`, meta
 * description, canonical link, and OG/Twitter title/description when a
 * page mounts. Zero dependencies, no react-helmet needed.
 *
 * Optional `breadcrumbs` prop inserts a BreadcrumbList JSON-LD script
 * for the current route, scoped so route changes remove the stale one.
 *
 *   useSeoTitle({
 *     title: "Worlds — TalesTalesTales",
 *     description: "Browse curated fantasy realms...",
 *     path: "/worlds",
 *     breadcrumbs: [
 *       { name: "Home", path: "/" },
 *       { name: "Worlds", path: "/worlds" },
 *     ],
 *   });
 */
const DEFAULT_SITE_URL = "https://talestalestales.com";
const BASE_URL = (process.env.REACT_APP_SITE_URL || DEFAULT_SITE_URL).replace(/\/$/, "");

function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [key, val] = selector.replace(/^meta\[|\]$/g, "").split("=");
    el.setAttribute(key, val.replace(/["']/g, ""));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeoTitle({ title, description, path, breadcrumbs, noindex }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (path) {
      const canonical = path.startsWith("http") ? path : `${BASE_URL}${path}`;
      setLink("canonical", canonical);
      setMeta('meta[property="og:url"]', "content", canonical);
    }

    // Robots — allow the private funnel (Play, Dashboard, admin) to opt out
    // of indexing even though React SPA renders them client-side. On public
    // pages we make sure any leftover noindex from a previous route is
    // reset back to the default.
    setMeta(
      'meta[name="robots"]',
      "content",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1"
    );

    // BreadcrumbList JSON-LD (optional). We tag the script with the
    // current path so navigating away tears it down cleanly.
    let breadcrumbScript = null;
    if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0 && path) {
      const payload = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": crumb.name,
          "item": `${BASE_URL}${crumb.path}`,
        })),
      };
      breadcrumbScript = document.createElement("script");
      breadcrumbScript.type = "application/ld+json";
      breadcrumbScript.setAttribute("data-seo-breadcrumbs-path", path);
      breadcrumbScript.textContent = JSON.stringify(payload);
      document.head.appendChild(breadcrumbScript);
    }

    return () => {
      if (path) {
        document.head
          .querySelectorAll(`script[data-seo-breadcrumbs-path="${path}"]`)
          .forEach((n) => n.remove());
      }
    };
  }, [title, description, path, breadcrumbs, noindex]);
}

// Re-export dates so pages can inject `datePublished` / `dateModified`
// into their own structured-data snippets without importing from two
// separate modules.
export { CONTENT_FIRST_PUBLISHED, CONTENT_LAST_UPDATED };
